import { ref } from 'vue'
import { toast } from 'vue3-toastify'
import { useI18n } from 'vue-i18n'

type MediaType = 'photo' | 'video'

interface UploadedFile extends File {
  file_url?: string
  signed_url?: string
  file_name?: string
  uploaded_url?: string
  media_type?: MediaType
}

interface UploadedMedia {
  file_name: string
  media_type: MediaType
}

interface SignedUrlResponse {
  fileUrl: string
  filename: string
  signedUrl: string
}

export const useFileUpload = () => {
  const uploadedFiles = ref<UploadedFile[]>([])
  const isUploading = ref(false)
  const hasExceededLimit = ref(false)
  const { t } = useI18n()

  // get signed AWS url using internal server api
  // filepath - /server/api/signed-url.post.ts
  const getSignedUrl = async (file: File): Promise<SignedUrlResponse> => {
    try {
      const { data } = await useAsyncData<SignedUrlResponse>('signedUrl', () =>
        $fetch('/api/signed-url/', {
          method: 'POST',
          body: { filename: file.name },
        })
      )
      return data.value as SignedUrlResponse
    } catch (error) {
      console.error('Error in getSignedUrl:', error)
      throw error
    }
  }

  const uploadFileToSignedUrl = async (file: File, signedUrl: string) => {
    await $fetch(signedUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
      },
      body: file,
    }).catch((error) => {
      console.error(error)
      toast(error, {
        type: 'error',
        position: toast.POSITION.TOP_CENTER,
      })
    })
  }

  const handleFileUpload = async (event: Event, maxFiles = 5) => {
    const input = event.target as HTMLInputElement
    hasExceededLimit.value = false

    if (!input.files?.length) return

    const files = Array.from(input.files)
    const remainingSlots = maxFiles - uploadedFiles.value.length
    const filesToUpload = files.slice(0, remainingSlots)

    if (files.length > remainingSlots) {
      hasExceededLimit.value = true
      toast(t('review.upload.maxFiles'), {
        type: 'error',
        position: toast.POSITION.TOP_CENTER,
      })
    }

    if (filesToUpload.length === 0) return

    isUploading.value = true

    try {
      for (const file of filesToUpload) {
        if (
          !file.type.startsWith('image/') &&
          !file.type.startsWith('video/')
        ) {
          throw new Error('Wrong file type')
        }

        const signedUrlResponse = await getSignedUrl(file)

        if (signedUrlResponse) {
          try {
            await uploadFileToSignedUrl(file, signedUrlResponse.signedUrl)

            const uploadedFile = file as UploadedFile
            uploadedFile.file_url = signedUrlResponse.fileUrl
            uploadedFile.file_name = signedUrlResponse.filename
            uploadedFile.signed_url = signedUrlResponse.signedUrl
            uploadedFile.media_type = file.type.startsWith('image/')
              ? 'photo'
              : 'video'
            uploadedFiles.value.push(uploadedFile)
          } catch (uploadError) {
            console.error('Error during file upload:', uploadError)
            toast(t('review.upload.error'), {
              type: 'error',
              position: toast.POSITION.TOP_CENTER,
            })
          }
        }
      }
    } catch (error) {
      toast(t('review.upload.error'), {
        type: 'error',
        position: toast.POSITION.TOP_CENTER,
      })
    } finally {
      isUploading.value = false
      input.value = ''
    }
  }

  const removeFile = (index: number) => {
    uploadedFiles.value.splice(index, 1)
  }

  const getFileUrl = (file: File) => window.URL.createObjectURL(file)

  const getUploadedMedia = (): UploadedMedia[] => {
    return uploadedFiles.value.map((file) => ({
      file_name: file.file_name || '',
      media_type: file.media_type || 'photo',
    }))
  }

  return {
    uploadedFiles,
    isUploading,
    hasExceededLimit,
    handleFileUpload,
    removeFile,
    getFileUrl,
    getUploadedMedia,
  }
}
